import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import {
  faBan,
  faCheck,
  faEye,
  faPen,
  faPlus,
  faPlusCircle,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from "@nebular/theme";
import {
  ColumnModel,
  FilterSettingsModel,
  GridComponent,
  PageSettingsModel,
} from "@syncfusion/ej2-angular-grids";
import { forkJoin } from "rxjs";
import { ErrorService } from '../../../../@core/utils/error.service';
import { KmlService } from "../../../../@core/utils/kml.service";
import { SessionService } from "../../../../@core/utils/session.service";
import {
  ConfirmationData,
  ConfirmationDialogComponent,
} from "../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component";
import { StorageTypeDTO } from '../../../modules/pick-plan/models/storage-types-dto';
import { StorageService } from '../../services/storage.service';
import { KwFilterDropdownService } from '../../widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { StorageDetailsComponent } from './storage-details/storage-details.component';

@Component({
  selector: 'storage-list',
  templateUrl: './storage-list.component.html',
  styleUrls: ['./storage-list.component.css']
})
export class StorageListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  // public editStorage: StorageTypeDTO = {};
  // public storageId: number;

  //#region Properties
  public editActive = false;
  public storageLoading = true;
  public storageTypeId: number;
  public submitting: any;
  public allStorageTypes: StorageTypeDTO[] = [];
  public savingStorageTypeIds: Set<number> = new Set<number>();
  public editStorageType: StorageTypeDTO = {};
  public allColumns: ColumnModel[] = [];
public gridName: string = "StorageTypeConfigGrid";
public highlightedStorageTypeId: number = null;
private pendingScrollToStorageTypeId: number = null;
public pageSettings: PageSettingsModel = {
  pageSize: 50,
  pageSizes: [50, 100, 200],
};
public filterSettings: FilterSettingsModel = {
  mode: "Immediate",
  type: "Excel",
};
public toolbarOptions: string[] = [
  "ExcelExport",
];
public enablePersistence: boolean = true;
public filterCount: number = 0;
  //#endregion
  
//#region Icon Properties
public faPlus = faPlus;
public faPen = faPen;
public faEye = faEye;
public faTrash = faTrash;
public faPlusCircle = faPlusCircle;
public faTimes = faTimes;
public faCheck = faCheck;
public faBan = faBan;
//#endregion

//#region ViewChild Properties
@ViewChild("grid", { static: true }) grid: GridComponent;

@ViewChild("actionTemplate", { static: true })
public actionTemplate: any;

@ViewChild("isActiveTemplate", { static: true })
public isActiveTemplate: any;
//#endregion

  constructor(
    private storageService: StorageService,
    private errorService: ErrorService,
    private kmlService: KmlService,
    private toastrService: NbToastrService,
    private sessionService: SessionService,
    public dialogService: MatDialog,
    private filterService: KwFilterDropdownService
  ){}


  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  //displayedColumns = ['name', 'description', 'isActive', 'integrationId', 'edit'];

  ngOnInit() {
    this.loadData();
  }

  loadData(){
    this.storageLoading = true;

    forkJoin([
      this.storageService.getStorageTypes(true),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {next:
      (results) => {

        this.allStorageTypes = results[0];

        this.storageLoading = false;
        this.setColumns();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.storageLoading = false;
      }}
    );
  }

  setColumns() {
    this.allColumns = [];

    // Hidden primary key column - lets us look up/select a specific row
    // (e.g. to scroll to and highlight a newly created storage type).
    this.allColumns.push({
      field: "id",
      isPrimaryKey: true,
      visible: false,
    });

    this.allColumns.push(
      ...[
        {
          field: "name",
          headerText: "Storage Type Name",
        },
        {
          field: "description",
          headerText: "Description",
        },
        {
          field: "integrationId",
          headerText: "Integration Id",
        },    
        {
          headerText: "Action",
          template: this.actionTemplate,
          width: 200,
        },
      ]
    );
  }
  onActionComplete(args: any) {
    if (args.requestType === 'filtering') {
      this.updateFilterCount();
    }
  }

  onGridDataBound() {
    this.updateFilterCount();

    if (this.pendingScrollToStorageTypeId != null) {
      // goToPage() can fire this from a render that still has stale data
      // (queued just ahead of the refresh that actually adds the row), so
      // only clear the pending id once we've actually found and scrolled to
      // it - otherwise keep waiting for the next dataBound to try again.
      const id = this.pendingScrollToStorageTypeId;
      const rowIndex = this.grid.getRowIndexByPrimaryKey(id);
      if (rowIndex > -1) {
        this.scrollGridRowIntoView(this.grid.getRowByIndex(rowIndex));
        this.pendingScrollToStorageTypeId = null;
        this.startHighlightFade(this.grid.getRowByIndex(rowIndex), id);

        // Something (sticky header repositioning, etc.) can quietly reset
        // the content scroll a moment after this - reassert it once more
        // shortly after to counteract that.
        setTimeout(() => {
          const idx = this.grid.getRowIndexByPrimaryKey(id);
          if (idx > -1) {
            const row = this.grid.getRowByIndex(idx);
            this.scrollGridRowIntoView(row);
            this.startHighlightFade(row, id);
          }
        }, 200);
      }
    }
  }

  private updateFilterCount() {
    const cols = (this.grid && this.grid.filterSettings && this.grid.filterSettings.columns) ? this.grid.filterSettings.columns : [];
    const uniqueFields = cols.map(c => c.field as string).filter((f: string, i: number, a: string[]) => a.indexOf(f) === i);
    this.filterCount = uniqueFields.length;
  }

  clearFilters() {
    window.localStorage.setItem("gridStorageTypeConfigFilters", "");
    this.grid.clearFiltering();
    this.filterCount = 0;
  }

  toolbarClick(args) {
    this.storageLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.item.properties.text == "Excel Export") {
      this.grid
        .excelExport({
          fileName: `StorageTypeExport.xlsx`,
        })
        .then(() => {
          this.storageLoading = false;
        });
    }
  }

  confirmDelete(id: number) {
    const confirmDialogData: ConfirmationData = {
      title: "Confirm Deletion",
      confirmMessage: "Are you sure you want to delete this storage type?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      displayComment: false,
      isWarning: false,
    };

    const confirmDialog = this.dialogService.open(ConfirmationDialogComponent, {
      width: "400px",
      data: confirmDialogData,
    });

    confirmDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result === true) {
        this.deleteRec(id);
      }
    });
  }

  deleteRec(id: number) {
    this.storageLoading = true;
    this.storageService.deleteStorageTypeDTO(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {next:() => {
        // Keep the filter dropdown cache in sync so other pages drop the deleted storage type immediately
        this.filterService.removeCached('storageType', id);
        this.toastrService.info("Storage type successfully deleted!");
        this.storageLoading = false;
        this.loadData();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.storageLoading = false;
      }}
    );
  }


  openDialog(IsEdit: boolean, data?: StorageTypeDTO) {
    const storageTypeDetailsDialog = this.dialogService.open(StorageDetailsComponent, {
      width: "500px",
    });
    if (IsEdit) {
      var persistData = (this.grid as GridComponent).getPersistData();
      window.localStorage.setItem("grid" + this.gridName, persistData);
      // Clone: never hand the dialog a live reference into allStorageTypes, or its
      // two-way-bound form fields would mutate the grid row before Save.
      this.sessionService.selectedStorageType = { ...data };
    } else {
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedStorageType = {} as StorageTypeDTO;
    }

    storageTypeDetailsDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result && result.backgroundSave && result.storageType) {
        if (result.isNew) {
          this.createStorageTypeInBackground(result.storageType);
        } else {
          this.saveStorageTypeInBackground(result.storageType);
        }
      } else if (result) {
        this.loadData();
      }
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedStorageType = {} as StorageTypeDTO;
    });
  }

  private saveStorageTypeInBackground(storageType: StorageTypeDTO): void {
    const originalStorageType = this.allStorageTypes.find((s) => s.id === storageType.id);
    this.savingStorageTypeIds.add(storageType.id);
    this.insertOrReplaceStorageTypeRow(storageType);
    this.refreshGridSoon();

    this.storageService.putStorageTypeDTO(storageType).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updated) => {
        this.savingStorageTypeIds.delete(storageType.id);
        // 204 No Content leaves `updated` null/undefined - fall back to the storage type we sent.
        this.filterService.upsertCached('storageType', updated || storageType);
        this.insertOrReplaceStorageTypeRow(updated || storageType);
        this.refreshGridSoon();
        this.toastrService.success(
          "Storage Type Details successfully updated!",
          storageType.name + " updated!"
        );
      },
      error: (err) => {
        this.savingStorageTypeIds.delete(storageType.id);
        if (originalStorageType) {
          this.insertOrReplaceStorageTypeRow(originalStorageType);
        }
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to update Storage Type "' + storageType.name + '". Please try again.',
          "Save failed"
        );
      }
    });
  }

  private createStorageTypeInBackground(storageType: StorageTypeDTO): void {
    // The storage type doesn't exist server-side yet, so show a temporary
    // placeholder row (negative id so it can't collide with a real one)
    // with the saving spinner until the server assigns a real id.
    const tempId = -Date.now();
    this.insertOrReplaceStorageTypeRow({ ...storageType, id: tempId });
    this.savingStorageTypeIds.add(tempId);

    this.storageService.postStorageTypeDTO(storageType).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        this.removeStorageTypeRow(tempId);
        this.savingStorageTypeIds.delete(tempId);
        this.filterService.upsertCached('storageType', created);
        this.insertOrReplaceStorageTypeRow(created);
        this.jumpToStorageTypeRow(created.id);
        this.toastrService.success(
          "Storage Type Details successfully saved!",
          created.name + " saved!"
        );
      },
      error: (err) => {
        this.removeStorageTypeRow(tempId);
        this.savingStorageTypeIds.delete(tempId);
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to create Storage Type "' + (storageType.name || "") + '". Please try again.',
          "Create failed"
        );
      }
    });
  }

  private insertOrReplaceStorageTypeRow(storageType: StorageTypeDTO): void {
    const index = this.allStorageTypes.findIndex((s) => s.id === storageType.id);
    const next = this.allStorageTypes.slice();
    if (index !== -1) {
      next[index] = storageType;
    } else {
      next.push(storageType);
    }
    this.allStorageTypes = next;
    this.refreshGridSoon();
  }

  // Defers the grid refresh to the next macrotask so Angular has already
  // pushed the updated allStorageTypes/savingStorageTypeIds state into the grid's
  // own [dataSource] binding before we force it to re-render.
  private refreshGridSoon(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.refresh();
      }
    });
  }

  private removeStorageTypeRow(id: number): void {
    this.allStorageTypes = this.allStorageTypes.filter((s) => s.id !== id);
    this.refreshGridSoon();
  }

  private isDefaultSortActive(): boolean {
    const cols = (this.grid && this.grid.sortSettings && this.grid.sortSettings.columns) || [];
    return cols.length === 0;
  }

  private jumpToStorageTypeRow(id: number): void {
    this.highlightedStorageTypeId = id;
    // Scrolling happens once the grid actually finishes its next render
    // (see onGridDataBound) rather than after a guessed delay.
    this.pendingScrollToStorageTypeId = id;

    if (!this.grid || !this.isDefaultSortActive()) {
      // Can't reliably compute which page the row lands on under a custom
      // sort - it'll still get highlighted/scrolled to whenever it's next
      // rendered on whatever page the user navigates to.
      return;
    }

    // Defer past the refresh(es) already queued by insertOrReplaceStorageTypeRow/
    // removeStorageTypeRow so this pages using the grid's up-to-date data.
    setTimeout(() => {
      const index = this.allStorageTypes.findIndex((s) => s.id === id);
      if (index === -1) {
        return;
      }

      const pageSize = this.pageSettings.pageSize || this.allStorageTypes.length;
      const page = Math.floor(index / pageSize) + 1;

      if (this.grid.pageSettings.currentPage !== page) {
        this.grid.goToPage(page);
      }
    });
  }

  // Scrolls the grid's own internal content viewport, not the page - a
  // plain row.scrollIntoView() walks every scrollable ancestor (including
  // the page), which can move the page while leaving the grid's own
  // internal scroll area untouched.
  private scrollGridRowIntoView(row: Element): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    const scrollableContent = this.grid.getContent().querySelector(".e-content") as HTMLElement;
    if (scrollableContent) {
      const targetScrollTop = rowElement.offsetTop
        - (scrollableContent.clientHeight / 2)
        + (rowElement.offsetHeight / 2);
      scrollableContent.scrollTo({ top: Math.max(targetScrollTop, 0), behavior: "auto" });
    } else if (typeof rowElement.scrollIntoView === "function") {
      rowElement.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }

  // Applies the green highlight directly (and times its removal) right when
  // the row is actually visible.
  private startHighlightFade(row: Element, id: number): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    rowElement.classList.add("row-highlight-new");
    if (this.highlightedStorageTypeId === id) {
      this.highlightedStorageTypeId = null;
    }
    setTimeout(() => {
      rowElement.classList.remove("row-highlight-new");
    }, 2000);
  }

  onRowDataBound(args: any): void {
    if (args.data && this.savingStorageTypeIds.has(args.data.id)) {
      args.row.classList.add('row-saving');
    } else if (args.row) {
      args.row.classList.remove('row-saving');
    }
    if (args.data && this.highlightedStorageTypeId != null && args.data.id === this.highlightedStorageTypeId) {
      args.row.classList.add('row-highlight-new');
    }
  }
  downloadFiles(args:string){
        this.storageLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.includes("Excel")) {
      this.grid
        .excelExport({
          fileName: `StorageTypeExport.xlsx`,
        })
        .then(() => {
          this.storageLoading = false;
        });
    }
  }


}
